import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Coins, Diamond, Heart, BookOpen, Sparkle, Users, ArrowRight, Check,
} from "@phosphor-icons/react";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";

const ICONS = { Coins, Diamond, Heart, BookOpen };

function formatMembers(n) {
  // 11,558 — matches the user's requested style.
  return Number(n || 0).toLocaleString("en-IN");
}

export default function CommunityHomePage() {
  const [me, setMe] = useState(null);
  const [groups, setGroups] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(false));
  }, []);

  useEffect(() => {
    if (me === null || me === false) return;
    api.get("/forum/groups").then(({ data }) => setGroups(data.items)).catch(() => setGroups([]));
  }, [me]);

  if (me === false) {
    return (
      <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
        <Header />
        <main className="flex-1 max-w-2xl mx-auto px-4 py-16 text-center">
          <Sparkle size={48} weight="duotone" className="mx-auto text-gold mb-4" />
          <h1 className="font-serif-display text-3xl">Sign in to join the community</h1>
          <p className="text-muted mt-3">Our astrology groups are open to all signed-in seekers.</p>
          <Link to="/login" className="inline-block mt-6 btn-primary rounded-full px-6 py-2.5">Sign in</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const handleJoin = async (slug) => {
    try {
      const { data } = await api.post(`/forum/groups/${slug}/join`);
      setGroups((gs) => gs.map((g) => (g.slug === slug ? { ...g, is_member: true, members: data.members } : g)));
      toast.success("You're in — welcome to the community.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join. Please retry.");
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col" data-testid="community-home">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-10">
        <div className="mb-6 sm:mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-1.5">
            <Sparkle size={12} weight="duotone" /> Astrology Community Group
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light text-[#3F2D1B]">Find your tribe</h1>
          <p className="text-sm text-muted mt-2 max-w-2xl leading-relaxed">
            Pick a community that matches what you&apos;re curious about today. Ask anything, share wins,
            help others — the more you give, the more the cosmos returns.
          </p>
        </div>

        {!groups ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-64 rounded-3xl bg-white/40 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {groups.map((g) => (
              <GroupCard
                key={g.slug}
                group={g}
                onJoin={() => handleJoin(g.slug)}
                onOpen={() => navigate(`/forum?group=${g.slug}`)}
              />
            ))}
          </div>
        )}

        <div className="mt-10 text-xs text-muted text-center">
          <Sparkle size={12} weight="duotone" className="inline mr-1" />
          Posts with images go through a brief super-admin approval to keep every group spam-free. Text posts publish instantly.
        </div>
      </main>
      <Footer />
    </div>
  );
}

function GroupCard({ group, onJoin, onOpen }) {
  const Icon = ICONS[group.icon] || Sparkle;
  return (
    <article
      className="group relative rounded-3xl overflow-hidden border border-soft bg-white shadow-sm hover:shadow-xl transition-all"
      data-testid={`group-card-${group.slug}`}
    >
      {/* Cover — themed banner photo with a colour overlay so text/badges
          stay readable across photos. Bumped to h-40/h-44 so the imagery
          actually breathes. */}
      <div
        className="h-40 sm:h-44 relative flex items-end p-5 bg-cover bg-center"
        style={{ backgroundImage: `url('/community/${group.slug}.jpg')` }}
      >
        {/* Gradient tint per group + dark vignette for legible badge text */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${group.cover_from}66 0%, ${group.cover_to}80 100%), linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.45) 100%)`,
          }}
        />
        <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/15 blur-2xl" />
        <div className="absolute right-4 top-4 px-2 py-1 rounded-full bg-black/40 backdrop-blur text-[10px] uppercase tracking-widest text-white font-semibold flex items-center gap-1">
          <Users size={11} weight="bold" />
          {formatMembers(group.members)} members
        </div>
        <div className="relative w-14 h-14 rounded-2xl bg-white/95 flex items-center justify-center shadow-lg ring-1 ring-white/40">
          <Icon size={26} weight="duotone" style={{ color: group.cover_from }} />
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <h3 className="font-serif-display text-xl text-[#3F2D1B]" data-testid={`group-name-${group.slug}`}>
          {group.name}
        </h3>
        {group.tagline && (
          <p className="text-xs text-muted italic mt-1">{group.tagline}</p>
        )}
        <p className="text-sm text-stone-700 mt-3 leading-relaxed line-clamp-3">{group.description}</p>

        <div className="mt-5 flex items-center gap-2">
          {group.is_member ? (
            <>
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
                <Check size={12} weight="bold" /> Joined
              </span>
              <button
                onClick={onOpen}
                className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold shadow-md hover:opacity-95"
                style={{ background: `linear-gradient(135deg, ${group.cover_from} 0%, ${group.cover_to} 100%)` }}
                data-testid={`open-${group.slug}`}
              >
                Open group <ArrowRight size={14} weight="bold" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onJoin}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-md hover:opacity-95"
                style={{ background: `linear-gradient(135deg, ${group.cover_from} 0%, ${group.cover_to} 100%)` }}
                data-testid={`join-${group.slug}`}
              >
                Join now
              </button>
              <button
                onClick={onOpen}
                className="text-xs text-stone-600 hover:text-stone-900 underline-offset-2 hover:underline"
                data-testid={`preview-${group.slug}`}
              >
                Preview posts →
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}
